import { useMemo } from 'react'
import { Map } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import type { Resource, ResourceCategory } from '@/types/domain'

interface ResourceSelectionHeaderProps {
    categories: (ResourceCategory & { resources: Resource[] })[]
    selectedResourceId: string | null
    onSelect: (resourceId: string | null) => void
    className?: string
}

export function ResourceSelectionHeader({
    categories,
    selectedResourceId,
    onSelect,
    className,
}: ResourceSelectionHeaderProps) {
    const allResources = useMemo(() => categories.flatMap(c => c.resources ?? []), [categories])

    const selectedResource = useMemo(
        () => allResources.find((r) => r.id === selectedResourceId),
        [allResources, selectedResourceId]
    )

    const selectedCategory = useMemo(
        () => selectedResource ? categories.find((c) => c.id === selectedResource.category_id) : undefined,
        [categories, selectedResource]
    )

    const currentCategoryResources = useMemo(() => selectedCategory?.resources ?? [], [selectedCategory?.resources])

    const canvasBounds = useMemo(() => {
        if (selectedCategory?.visual_type !== 'canvas' || currentCategoryResources.length === 0) {
            return { w: 0, h: 0 }
        }
        const PAD = 8
        const w = Math.max(...currentCategoryResources.map(r => r.x + (r.width ?? 80))) + PAD
        const h = Math.max(...currentCategoryResources.map(r => r.y + (r.height ?? 50))) + PAD
        return { w, h }
    }, [selectedCategory, currentCategoryResources])

    if (allResources.length === 0) return null

    return (
        <div className={cn('flex flex-col gap-2 rounded-xl border bg-muted/30 p-3', className)}>
            {/* Row 1: Category pills */}
            <div className="flex items-center gap-3 flex-wrap">
                <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground whitespace-nowrap uppercase tracking-wider">
                    <Map className="h-3.5 w-3.5" />
                    Bereich:
                </div>
                <div className="flex flex-wrap gap-1.5">
                    <Button
                        variant={selectedResourceId === null ? 'default' : 'outline'}
                        size="sm"
                        aria-pressed={selectedResourceId === null}
                        onClick={() => onSelect(null)}
                        className="h-7 rounded-full text-xs"
                    >
                        Alle
                    </Button>
                    {categories.map((category) => (
                        <Button
                            key={category.id}
                            aria-pressed={selectedCategory?.id === category.id}
                            variant={selectedCategory?.id === category.id && selectedResourceId !== null ? 'secondary' : 'outline'}
                            size="sm"
                            onClick={() => {
                                const first = (category.resources ?? [])[0]
                                if (first) onSelect(first.id)
                            }}
                            className="h-7 rounded-full text-xs"
                        >
                            {category.name}
                        </Button>
                    ))}
                </div>
            </div>

            {/* Row 2: Individual resource buttons — only shown when a category is selected */}
            {selectedCategory && selectedResourceId !== null && currentCategoryResources.length > 0 && (
                <div className="pl-1">
                    {selectedCategory.visual_type === 'canvas' ? (
                        /* Canvas: sized to fit contents */
                        <div className="overflow-auto rounded-lg border bg-background">
                            <div className="relative" style={{ width: canvasBounds.w, height: canvasBounds.h }}>
                                {currentCategoryResources.map((resource) => (
                                    <button
                                        key={resource.id}
                                    aria-pressed={selectedResourceId === resource.id}
                                        onClick={() => onSelect(resource.id)}
                                        className={cn(
                                            'absolute flex items-center justify-center rounded-md border text-[10px] font-bold transition-all hover:scale-105 active:scale-95 uppercase tracking-tighter',
                                            selectedResourceId === resource.id
                                                ? 'bg-primary text-primary-foreground border-primary shadow-md z-10'
                                                : 'bg-muted hover:bg-muted/80 border-border text-muted-foreground hover:text-foreground'
                                        )}
                                        style={{
                                            left: `${resource.x}px`,
                                            top: `${resource.y}px`,
                                            width: `${resource.width ?? 80}px`,
                                            height: `${resource.height ?? 50}px`,
                                        }}
                                    >
                                        {resource.name}
                                    </button>
                                ))}
                            </div>
                        </div>
                    ) : selectedCategory.visual_type === 'grid' ? (
                        /* Grid: 4-column grid matching the admin layout */
                        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4">
                            {currentCategoryResources.map((resource) => (
                                <button
                                    key={resource.id}
                                    aria-pressed={selectedResourceId === resource.id}
                                    onClick={() => onSelect(resource.id)}
                                    className={cn(
                                        'flex items-center justify-center rounded-lg border p-3 text-sm font-medium transition-all hover:shadow-md',
                                        selectedResourceId === resource.id
                                            ? 'bg-primary text-primary-foreground border-primary shadow-sm'
                                            : 'bg-card border-border text-foreground hover:border-primary/50'
                                    )}
                                >
                                    {resource.name}
                                </button>
                            ))}
                        </div>
                    ) : (
                        /* List: one full-width row per resource */
                        <div className="flex flex-col gap-1.5">
                            {currentCategoryResources.map((resource) => (
                                <button
                                    key={resource.id}
                                    aria-pressed={selectedResourceId === resource.id}
                                    onClick={() => onSelect(resource.id)}
                                    className={cn(
                                        'flex w-full items-center gap-3 rounded-md border px-3 py-2 text-sm font-medium text-left transition-colors',
                                        selectedResourceId === resource.id
                                            ? 'bg-primary text-primary-foreground border-primary'
                                            : 'bg-card border-border text-foreground hover:bg-muted'
                                    )}
                                >
                                    {resource.name}
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    )
}
